import React, { useState, useMemo, useEffect } from 'react';
import { FillingBatch } from '../../../types/fillingBatch';
import { Recipe } from '../../../types/recipe';
import { FillingIngredient, FillingComponentBatch } from '../../../types/fillingRecord';
import { FillingRecordCard } from './FillingRecordCard';
import { createBlankFillingBatch } from '../../../store/slices/fillingBatchSlice';
import { groupItemsBySection } from '../../../utils/haccpSectionUtils';
import { HaccpSectionHeader } from '../../../components/haccp/HaccpSectionHeader';
import { matchesSearchQuery } from '../../../utils/searchUtils';

interface FillingRecordListProps {
  records: FillingBatch[];
  fillings: Recipe[];
  onAdd: (record: FillingBatch, ingredients: FillingIngredient[], componentBatches: FillingComponentBatch[]) => void;
  onUpdate: (record: FillingBatch) => void;
  onDelete: (recordId: string) => void;
  onTimesProducedChange?: (id: string, timesProduced: number) => void;
}

export const FillingRecordList: React.FC<FillingRecordListProps> = ({ records, fillings, onAdd, onUpdate, onDelete, onTimesProducedChange }) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newRecord, setNewRecord] = useState<FillingBatch>(createBlankFillingBatch());
  const [searchTerm, setSearchTerm] = useState('');

  // Filter records based on search
  const filteredRecords = useMemo(() => {
    if (!searchTerm.trim()) return records;
    return records.filter(record => matchesSearchQuery(record, searchTerm, [record.productName || '']));
  }, [records, searchTerm]);

  // Group records into sections by date
  const sections = useMemo(
    () => groupItemsBySection(filteredRecords, (record) => record.dateOfProduction),
    [filteredRecords]
  );

  // Track expanded/collapsed state for each section (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const state: Record<string, boolean> = {};
    for (const section of sections) {
      state[section.key] = false;
    }
    return state;
  });

  // Update expanded state when sections change (e.g., new month appears)
  useEffect(() => {
    setExpandedSections((prev) => {
      const newState = { ...prev };
      let hasChanges = false;
      for (const section of sections) {
        if (!(section.key in newState)) {
          newState[section.key] = false;
          hasChanges = true;
        }
      }
      return hasChanges ? newState : prev;
    });
  }, [sections]);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewRecord(createBlankFillingBatch());
  };

  const handleSaveNew = (record: FillingBatch, ingredients: FillingIngredient[], componentBatches: FillingComponentBatch[]) => {
    // Validate required fields
    if (!record.productId || !record.dateOfProduction || !record.preparedBy || !record.verifiedBy) {
      alert('Product, Date of Production, Prepared By, and Verified By are required fields.');
      return;
    }

    onAdd({
      ...record,
      updatedAt: new Date().toISOString()
    }, ingredients, componentBatches);
    setIsAddingNew(false);
    setNewRecord(createBlankFillingBatch());
  };

  const handleCancelNew = () => {
    setIsAddingNew(false);
    setNewRecord(createBlankFillingBatch());
  };

  const handleEditToggle = (recordId: string) => {
    if (editingId === recordId) {
      setEditingId(null);
    } else {
      setEditingId(recordId);
    }
  };

  const handleUpdate = (record: FillingBatch) => {
    // Validate required fields
    if (!record.productId || !record.dateOfProduction || !record.preparedBy || !record.verifiedBy) {
      alert('Product, Date of Production, Prepared By, and Verified By are required fields.');
      return;
    }

    onUpdate({
      ...record,
      updatedAt: new Date().toISOString()
    });
    setEditingId(null);
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  return (
    <div>
      {/* Add New Filling Button */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={handleAddNew}
          disabled={isAddingNew || editingId !== null}
          style={{
            ...buttonStyle,
            backgroundColor: isAddingNew || editingId !== null ? '#cccccc' : '#4CAF50',
            color: 'white',
            cursor: isAddingNew || editingId !== null ? 'not-allowed' : 'pointer'
          }}
          onMouseEnter={e => {
            if (!isAddingNew && editingId === null) {
              e.currentTarget.style.backgroundColor = '#45a049';
            }
          }}
          onMouseLeave={e => {
            if (!isAddingNew && editingId === null) {
              e.currentTarget.style.backgroundColor = '#4CAF50';
            }
          }}
        >
          + Add New Filling
        </button>
      </div>

      {/* Search */}
      {!isAddingNew && !editingId && (
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Search filling records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
              boxSizing: 'border-box' as const
            }}
          />
        </div>
      )}

      {/* New Filling Record Card */}
      {isAddingNew && (
        <FillingRecordCard
          record={newRecord}
          fillings={fillings}
          isEditMode={true}
          onUpdate={() => {}}
          onSaveNew={handleSaveNew}
          onDelete={handleCancelNew}
          onEditToggle={handleCancelNew}
        />
      )}

      {/* Existing Filling Records grouped by section */}
      {records.length === 0 && !isAddingNew && (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            color: '#999',
            fontStyle: 'italic'
          }}
        >
          <p style={{ margin: 0, fontSize: '1rem' }}>No filling records yet. Click "Add New Filling" to create one.</p>
        </div>
      )}

      {/* No search results */}
      {records.length > 0 && filteredRecords.length === 0 && !isAddingNew && (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            color: '#999',
            fontStyle: 'italic'
          }}
        >
          <p style={{ margin: 0, fontSize: '1rem' }}>No filling records match your search for "{searchTerm}".</p>
        </div>
      )}

      {sections.map((section) => (
        <div key={section.key} style={{ marginBottom: '16px' }}>
          <HaccpSectionHeader
            label={section.label}
            itemCount={section.items.length}
            isExpanded={expandedSections[section.key] ?? !section.isTrialSection}
            isTrialSection={section.isTrialSection}
            onToggle={() => toggleSection(section.key)}
          />

          {(expandedSections[section.key] ?? !section.isTrialSection) && (
            <div>
              {section.items.map((record) => (
                <FillingRecordCard
                  key={record.id}
                  record={record}
                  fillings={fillings}
                  isEditMode={editingId === record.id}
                  onUpdate={handleUpdate}
                  onDelete={() => onDelete(record.id)}
                  onEditToggle={() => handleEditToggle(record.id)}
                  onTimesProducedChange={onTimesProducedChange}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
